import clsx from 'clsx';
import {
  type ComponentType,
  type LazyExoticComponent,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocalStorage, useMedia, useSessionStorage } from 'react-use';
import { useSearchParams } from 'wouter';
import { debounce } from './js';
import { addComma, fixShape, toDigits } from './number';
export const cn = clsx;

export function useSearchParam<I = string | undefined>(
  key: string,
  initialValue?: I,
) {
  const [searchParams, setSearchParams] = useSearchParams();

  const value = useMemo(
    () => (searchParams.get(key) ?? initialValue) as I,
    [searchParams, initialValue, key],
  );

  const setValue = useCallback(
    (newVal?: string) => {
      setSearchParams(
        (currentSearchParams) => {
          if (!newVal) {
            currentSearchParams.delete(key);
          } else {
            currentSearchParams.set(key, newVal);
          }
          return currentSearchParams;
        },
        {
          replace: true,
        },
      );
    },
    [key, setSearchParams],
  );

  return useMemo(() => [value, setValue] as const, [value, setValue]);
}

export function usePromise<M = void, T = void>() {
  const [isRunning, setIsRunning] = useState(false);
  const [params, setParams] = useState<M | null>(null);
  const [result, setResult] = useState<T>();
  const [error, setError] = useState<unknown>();
  const resolve = useRef<(value: T) => void>(() => {
    throw new Error('unexpected');
  });
  const reject = useRef<(value?: unknown) => void>(() => {
    throw new Error('unexpected');
  });

  const run = useCallback((newParams: M): Promise<T> => {
    setParams(newParams);
    setIsRunning(true);
    setError(undefined);
    return new Promise<T>((_resolve, _reject) => {
      resolve.current = _resolve;
      reject.current = _reject;
    })
      .then((_result) => {
        setResult(_result);
        return _result;
      })
      .catch((_error) => {
        setError(_error);
        throw _error;
      })
      .finally(() => {
        setIsRunning(false);
      });
  }, []);

  return useMemo(
    () => ({
      run,
      isRunning,
      result,
      error,
      resolve: resolve.current,
      reject: reject.current,
      params,
    }),
    [run, error, isRunning, params, result],
  );
}

export const useIsDesktop = () => useMedia('(min-width: 75rem)');

export const useFormatNumber = () => {
  const deFormat = (numStr: string) => {
    if (!numStr) return '0';
    return fixShape(toDigits(numStr));
  };

  const format = (number: string | number, sepByComma = true) => {
    if (!number) return '';
    const numStr = deFormat(number.toString());
    if (sepByComma) return addComma(numStr);
    return numStr;
  };

  return {
    format,
    deFormat,
  };
};

export const useLazyValue = <T>(value: T, lazyTime: number): T => {
  const [lazyValue, setLazyValue] = useState<T>(value);
  useEffect(() => {
    const deb = debounce(() => {
      setLazyValue(value);
    }, lazyTime);
    deb.run(undefined);
    return () => deb.clear();
  }, [lazyTime, value]);

  return lazyValue;
};

export const useControlledState = <T>(controlerState: T) => {
  const state = useState<T>(controlerState);
  useEffect(() => {
    state[1](controlerState);
  }, [controlerState, state[1]]);
  return state;
};

export type GlobalState<T extends object> = [
  T,
  (newValue?: Partial<T>) => void,
];
export type PageState<T extends object> = GlobalState<T>;

export const usePageState = <T extends object>(
  defaultValue: T,
): PageState<T> => {
  const [searchParams, setSearchParams] = useSearchParams();

  const data = Object.fromEntries(
    Object.entries(defaultValue).map(([k, v]) => [k, searchParams.get(k) ?? v]),
  ) as T;

  const setFullData = (newV: T) =>
    setSearchParams((p) => {
      Object.entries(newV).forEach(([k, v]) => {
        p.set(k, v);
      });
      return p;
    });

  const setData = (newData?: Partial<T>) => {
    let savingData = data;
    for (const [key, value] of Object.entries(newData ?? {})) {
      if (typeof value === 'undefined') continue;
      savingData = {
        ...savingData,
        [key]: value,
      };
    }
    setFullData(savingData as T);
  };
  return [data, setData] as const;
};

export const useGlobalState = <T extends object>(
  stateName: string,
  defaultValue: T,
  storage: 'local-storage' | 'session-storage' = 'local-storage',
): GlobalState<T> => {
  const [sessionStorageData, setSessionStorageData] = useSessionStorage<T>(
    stateName,
    defaultValue,
  );
  const [localStorageData, setLocalStorageData] = useLocalStorage<T>(
    stateName,
    defaultValue,
  );

  const data = {
    ...defaultValue,
    ...(storage === 'session-storage'
      ? sessionStorageData || {}
      : localStorageData || {}),
  };

  const setFullData =
    storage === 'session-storage' ? setSessionStorageData : setLocalStorageData;

  const setData = (newData?: Partial<T>) => {
    let savingData = data;
    for (const [key, value] of Object.entries(newData ?? {})) {
      if (typeof value === 'undefined') continue;
      savingData = {
        ...savingData,
        [key]: value,
      };
    }
    setFullData(savingData as T);
  };
  return [data, setData] as const;
};

export const getGlobalState = <T extends object>(
  stateName: string,
  defaultValue: T,
  storage: 'local-storage' | 'session-storage' = 'local-storage',
): T => {
  try {
    return {
      ...defaultValue,
      ...(JSON.parse(
        window[
          storage === 'session-storage' ? 'sessionStorage' : 'localStorage'
        ].getItem(stateName)!,
      ) || {}),
    };
  } catch {
    return defaultValue;
  }
};

const calcRemainingTime = (expire: number) =>
  Math.max(Math.ceil((expire - Date.now()) / 1000), 0);
export const useCountdown = () => {
  const [expire, setExpire] = useState(0);

  const [value, setValue] = useState(calcRemainingTime(expire));

  const start = useCallback((offset: number) => {
    setExpire(Date.now() + offset);
  }, []);

  const stop = useCallback(() => {
    setExpire(0);
  }, []);

  useEffect(() => {
    setValue(calcRemainingTime(expire));
    const interval = setInterval(() => {
      setValue(() => {
        const newValue = calcRemainingTime(expire);
        if (newValue <= 0) {
          clearInterval(interval);
        }
        return newValue;
      });
    }, 500);
    return () => clearInterval(interval);
  }, [expire]);

  return {
    value,
    start,
    stop,
  };
};

export function lazyComponent<TModule, TKey extends keyof TModule>(
  importFn: () => Promise<TModule>,
  exportName: TKey,
): LazyExoticComponent<ComponentType<any>> {
  return lazy(() =>
    importFn().then((module) => ({
      default: module[exportName] as ComponentType<any>,
    })),
  );
}
